/**
 * Mirrored from mju-ai-workbench shared/schemas/orgTeamsV2.ts at feat/teams-owner-schema 760ba398 (lands in Workbench 0.88.1; teams-one-owner owner and member id, D313 unmanaged shapes), with D410's words from next/0.90.1 02ee305d (a member's id required from the next release); only the four import lines differ (.js extensions, ./roleWords.js, ./orgTeamScope.js).
 *
 * Member schema v2 for the organisation's teams (D251, docs/plans/2026-10-team-parity.md): the
 * workbench's own shapes for what a team or specialist carries beyond `orgTeams.ts` (mirrored from
 * AIOE, byte for byte, and not edited here). The AIOE copies this file when it takes v2.
 *
 * Rules the shapes keep:
 *  - placement that belongs to one machine never travels: "this computer" (no `runsOn`), an endpoint
 *    URL, an ACP command line, a worktree path;
 *  - a picture travels as the character's index, `useCharacter` and an https address, never a data URL;
 *  - a key or an account travels by its name only; the secret stays in each machine's store.
 *
 * As AIOE 0.36 takes it (D251): nothing it already had is repurposed — `roleId` stays a catalogue
 * role entry and `skillIds` catalogue skills; v2 adds `roleKind` (the workbench's vocabulary), `role`
 * (the title, free text) and `skills` (free-text names for skills not in the catalogue). Support is
 * read from `teamSchema: 2` on `GET /portfolio/v1/me` and `memberSchema: 2` on every v2 answer. A
 * field that belongs to one computer answers 400 `field_not_allowed` naming its path; unknown keys
 * are dropped and listed as `ignored`; a field both sides changed since `ifVersion` stays as the
 * platform has it and is listed as `refused` (D237's merge).
 *
 * D273 (Foundry agents as team members) adds, additively: `FOUNDRY_API_VERSIONS`, the member's `foundry`
 * block (`orgTeamMemberFoundrySchema`, workbench-set; the newer `updatedAt` wins a merge), `foundry` in
 * `V2_ONLY_MEMBER_FIELDS` and its words, and the members list's refusal of `provider: "foundry"` with
 * `runsOn.cloud` (`FOUNDRY_NOT_IN_CLOUD`).
 *
 * D286 (the Teams page as one list) adds, additively: the team's `workbenchScope` — where the workbench
 * keeps it (`repository`, `project` or `machine`, shared/teams/orgTeamScope.ts), sent on every save and
 * changed when the person moves it — so the console shows the same categories and another machine adds
 * it in the right one. `origin` stays as set on create. D272 adds, additively, the team's `madeBy` (`designed`, `drafted`,
 * `taskId`, `runId`, `deviceId`): a specialist made with the designer says so on its record; kept once set. AIOE 0.47.0 stores and returns it (no longer `ignored`),
 * and adds `PUT /projects/v1/:id/teams/:teamId { "use": true | false }` (a project takes a team it can see, or stops
 * using it) and `POST /admin/v1/teams/:copyId/merge { into }` (an old copy folded into its original). The view's `TeamSharingStatus` gains `platformId`
 * (the link between a definition and its record), `conflict` and the `incoming` state.
 *
 * D272 part 3 (context-budget profiles) adds, additively: `contextProfile` (`ORG_CONTEXT_PROFILES`: `auto`, `lean`,
 * `standard`, `full`) on a member and on the team record — the member's and the team's own choice, sent by the sync
 * from the definition and read back field by field (in `V2_ONLY_MEMBER_FIELDS` for a member), so an organisation
 * team's choice reaches every machine. Absent and `auto` both mean "chosen per member".
 *
 * D410 (agent principals, AIOE's roles step 6): a member's `id` is required from the next release (see its words), and
 * every agent's principal is built from it (shared/rbac/agentPrincipal.ts). No shape changes in this release.
 */
import { z } from "zod";
import { orgTeamMemberSchema, orgTeamSchema } from "./orgTeams.js";
import { teamPlacementSchema } from "./projects.js";
import { ROLE_IDS, roleIdFromWords } from "./roleWords.js";
import { ORG_TEAM_WORKBENCH_SCOPES } from "./orgTeamScope.js";
export const ORG_TEAM_MEMBER_SCHEMA = 2;
/** The 41 pixel characters a member can be drawn as (D243), by index. */
export const MEMBER_CHARACTER_COUNT = 41;
export const MEMBER_THINKING_LEVELS = ["default", "low", "medium", "high", "max"];
/** A member's or a team's context-budget profile (D272 part 3); `auto` (or absent) is chosen per member. */
export const ORG_CONTEXT_PROFILES = ["auto", "lean", "standard", "full"];
export const MEMBER_PERMISSION_MODES = ["supervised", "auto-edits", "auto", "full"];
const name100 = z.string().min(1).max(100);
const relativePath = z
    .string()
    .min(1)
    .max(300)
    .refine((p) => !p.startsWith("/") && !p.includes("\\") && !/^[A-Za-z]:/.test(p) && !p.split("/").includes(".."), "a path inside the repository");
/** `runsOn` as v2 holds it: AIOE's placement plus a node pinned by its device id. Never both a node and a workbench. */
export const memberPlacementV2Schema = teamPlacementSchema
    .extend({ node: z.string().min(1).max(100).optional() })
    .strict()
    .refine((p) => !(p.node && p.device), { message: "names both a node and a workbench" });
/** The Foundry agent surfaces the workbench speaks (D273): `v1` is Foundry's GA agents API with Responses / Conversations. */
export const FOUNDRY_API_VERSIONS = ["v1"];
/**
 * A Foundry member's agent (D273, the workbench's docs/plans/2026-10-foundry-agents-workbench.md): a member
 * whose runtime is `foundry` names the Foundry project and model deployment it runs in, and the workbench
 * records here what it published there. Foundry's GA `v1` surface names agents by name and version: there
 * is no `agentId`. Always set by the workbench (it publishes; the platform only displays); a merge keeps
 * the block with the newer `updatedAt`.
 *
 *  - `projectEndpoint`: `https://<resource>.services.ai.azure.com/api/projects/<project>`.
 *  - `deployment`: the model deployment's name in that project.
 *  - `agentName` / `agentVersion`: the agent in Foundry, once published (`mojoup-<team>-<member>-<6 hex>`).
 *  - `apiVersion`: the Foundry surface it was published and runs on (`FOUNDRY_API_VERSIONS`).
 *  - `definitionDigest`: SHA-256 of the definition last published, so it is published again only on a change.
 *  - `publishedBy` / `updatedAt`: who published that version, and when (ISO).
 *  - `hostedTools`: Foundry's own tools (file_search, web_search …) the member asks for; the policy's
 *    `foundry.projects[].hostedTools` decides which are attached when it is published.
 *  - `adopted`: an agent someone built in the Foundry portal, run but never rewritten or deleted.
 *  - `entraAgentId`: the Entra agent identity Foundry reports for the agent, when it reports one.
 */
export const orgTeamMemberFoundrySchema = z.object({
    projectEndpoint: z.string().min(1).max(500).regex(/^https:\/\/\S+$/i, "an https address"),
    deployment: z.string().min(1).max(200),
    agentName: z.string().min(1).max(200).optional(),
    agentVersion: z.string().min(1).max(100).optional(),
    apiVersion: z.string().min(1).max(40).optional(),
    definitionDigest: z.string().min(1).max(100).optional(),
    publishedBy: z.string().min(1).max(200).optional(),
    updatedAt: z.string().max(40).optional(),
    hostedTools: z.array(z.string().min(1).max(100)).max(20).optional(),
    adopted: z.boolean().optional(),
    entraAgentId: z.string().regex(/^[0-9a-fA-F-]{8,64}$/).optional(),
});
/** Why a Foundry member is never placed In the cloud: placement says which machine drives it and holds the sign-in. */
export const FOUNDRY_NOT_IN_CLOUD = "A Foundry member runs from a computer or a node; In the cloud is for providers' coding agents.";
export const orgTeamMemberV2Schema = orgTeamMemberSchema.extend({
    /**
     * A stable id the workbench gives a member at create (teams-one-owner, decision 8), so a rename never loses the
     * member's identity or history. D410: **required from the next release.** The workbench now gives one to every member
     * that has none when a team is loaded or saved, at every scope, keeps it across renames (`formerNames` recorded as
     * before) and sends it on every save. It stays optional on the wire for this one release; then AIOE keys identities by
     * `<teamId>/<id>`: the member's principal is `agent:<teamId>/<id>` (shared/rbac/agentPrincipal.ts), and its
     * `entraAgentId` stays linked to it across a rename. A member a workbench loaded without an id takes
     * `m-` + the first 32 hex digits of SHA-256 of `<teamId>/<its first name>` (its oldest former name, else its name), so
     * every machine reading the same definition names it the same way before any of them saves it.
     */
    id: z.string().min(1).max(100).optional(),
    runsOn: memberPlacementV2Schema.optional(),
    formerNames: z.array(name100).max(20).optional(),
    /** The member's title, free text ("Backend Developer"). `roleId` stays the catalogue's role entry. */
    role: z.string().min(1).max(200).optional(),
    /** That role read as the workbench's vocabulary. */
    roleKind: z.enum(ROLE_IDS).optional(),
    /** Free-text names of skills the catalogue has no entry for; catalogue skills stay in `skillIds`. */
    skills: z.array(name100).max(50).optional(),
    tools: z.array(z.string().min(1).max(200)).max(100).optional(),
    avatar: z.number().int().min(0).max(MEMBER_CHARACTER_COUNT - 1).optional(),
    useCharacter: z.boolean().optional(),
    pictureUrl: z.string().max(2000).regex(/^https:\/\/\S+$/i, "an https address").optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    thinkingLevel: z.enum(MEMBER_THINKING_LEVELS).optional(),
    /** The member's own context-budget profile (D272 part 3). Workbench-set. */
    contextProfile: z.enum(ORG_CONTEXT_PROFILES).optional(),
    agentInstructions: z.string().max(32_000).optional(),
    useWorktree: z.boolean().optional(),
    permissionMode: z.enum(MEMBER_PERMISSION_MODES).optional(),
    cwd: relativePath.optional(),
    providerId: name100.optional(),
    credential: name100.optional(),
    account: name100.optional(),
    /** The specialist this member is, by its id on the platform. */
    ref: name100.optional(),
    azureDevOpsUser: z.object({ uniqueName: z.string().min(1).max(320), displayName: z.string().max(300).optional() }).strict().optional(),
    /** Set by the platform: whether Microsoft 365 Copilot may call this agent (D213). */
    a2a: z.object({ exposed: z.boolean() }).optional(),
    /** The member's Foundry agent, when its runtime (`provider`) is `foundry` (D273). Workbench-set. */
    foundry: orgTeamMemberFoundrySchema.optional(),
});
/** The team settings v2 carries whole, as the workbench's TeamConfig has them. */
export const orgTeamSettingsV2Schema = z.object({
    orchestration: z.record(z.string(), z.unknown()).optional(),
    approvals: z.record(z.string(), z.unknown()).optional(),
    permissionMode: z.enum(MEMBER_PERMISSION_MODES).optional(),
    worktreeCleanup: z.enum(["auto", "manual", "prompt"]).optional(),
    maxConcurrentPlans: z.number().int().min(1).max(20).optional(),
    taskTokenBudget: z.number().int().positive().optional(),
    research: z.record(z.string(), z.unknown()).optional(),
    reviewPolicy: z.enum(["none", "self", "human"]).optional(),
    guidelines: z.string().max(2000).optional(),
});
/**
 * Where a team was defined, set on create and never changed. `project`: a repository's or a project's
 * team; `organisation`: one the workbench keeps for every project (its machine-wide teams).
 * `definedBy` is a principal id (never shown), `definedByName` the display name (shown).
 */
export const orgTeamOriginSchema = z.object({
    projectId: z.string().min(1).max(200),
    scope: z.enum(["project", "organisation"]),
    definedBy: z.string().max(200).optional(),
    definedByName: z.string().max(200).optional(),
});
/**
 * D272 (additive): how a definition was made. `designed`: with the specialist designer in a conversation; `drafted`:
 * by a model from real work (`taskId`, `runId`, `deviceId` say which), the words D280's catalogue submissions use.
 * Workbench-set and kept field by field like a member's `foundry`: set from the definition, never cleared by a plain
 * edit. Not `origin`, which is the platform's (where it was defined, "Used by", the D286 grouping).
 */
export const orgTeamMadeBySchema = z.object({
    designed: z.literal(true).optional(),
    drafted: z.literal(true).optional(),
    taskId: z.string().min(1).max(100).optional(),
    runId: z.string().min(1).max(100).optional(),
    deviceId: z.string().min(1).max(100).optional(),
});
/**
 * One field's last change, as the platform keeps it (the newest 200). `path` is `name`,
 * `settings.guidelines`, `members.Coder.model`; `by` is the principal id (never shown), `byName` the name.
 */
export const orgTeamFieldChangeSchema = z.object({
    path: z.string().min(1).max(300),
    by: z.string().max(200),
    byName: z.string().max(200).optional(),
    at: z.string(),
});
/** The team's owner: a person, by id, with the display name the platform serves beside it. */
export const orgTeamOwnerSchema = z.object({ id: z.string().min(1).max(100), name: z.string().min(1).max(200).optional() });
export const orgTeamV2Schema = orgTeamSchema.extend({
    memberSchema: z.literal(ORG_TEAM_MEMBER_SCHEMA).optional(),
    kind: z.enum(["team", "specialist"]).optional(),
    // A Foundry member is driven by a computer or a node (D273): never placed In the cloud. On the list, so the object schemas stay extendable.
    members: z
        .array(orgTeamMemberV2Schema)
        .min(1)
        .max(50)
        .superRefine((members, ctx) => {
        members.forEach((m, i) => {
            if (m.provider === "foundry" && m.runsOn?.cloud)
                ctx.addIssue({ code: "custom", message: FOUNDRY_NOT_IN_CLOUD, path: [i, "runsOn", "cloud"] });
        });
    }),
    settings: orgTeamSettingsV2Schema.optional(),
    origin: orgTeamOriginSchema.optional(),
    /** How it was made (D272): workbench-set, kept on every later save. */
    madeBy: orgTeamMadeBySchema.optional(),
    /** The team's context-budget profile for members that choose none (D272 part 3). Workbench-set. */
    contextProfile: z.enum(ORG_CONTEXT_PROFILES).optional(),
    /** Where the workbench keeps it (D286): a repository's, a project's, or every project's on one computer. Workbench-set. */
    workbenchScope: z.enum(ORG_TEAM_WORKBENCH_SCOPES).optional(),
    changes: z.array(orgTeamFieldChangeSchema).max(200).optional(),
    /**
     * The person accountable for the team (teams-one-owner, decision 6): set by the platform on create from the caller
     * when a body has none, kept on every save, changed only by the owner or an administrator (403 `owner_only`), and the
     * Agent 365 sponsor of its identities. Optional on the wire for one release.
     */
    owner: orgTeamOwnerSchema.optional(),
});
/** A field both sides changed since `ifVersion`: it stands as the platform has it (`theirs`). */
export const orgTeamRefusedFieldSchema = z.object({ path: z.string().min(1).max(300), ours: z.unknown().optional(), theirs: z.unknown().optional() });
/**
 * A save's answer (200 either way): the stored team, the keys it dropped (`ignored`) and the fields it
 * kept as they were (`refused`). Only a change of `id`, `kind` or `memberSchema` answers 409
 * `version_conflict { current }`; a field that belongs to one computer, 400 `field_not_allowed { path }`;
 * deleting a team another project uses, 409 `shared_team` with those projects' names.
 */
export const orgTeamSaveAnswerSchema = orgTeamV2Schema.extend({
    ignored: z.array(z.string().max(300)).max(200).optional(),
    refused: z.array(orgTeamRefusedFieldSchema).max(200).optional(),
});
/** The member fields only v2 holds: kept on this computer until the platform answers `memberSchema: 2`. */
export const V2_ONLY_MEMBER_FIELDS = [
    "formerNames", "role", "skills", "tools", "avatar", "useCharacter", "color", "thinkingLevel", "agentInstructions",
    "useWorktree", "permissionMode", "cwd", "providerId", "credential", "account", "ref", "azureDevOpsUser", "foundry",
    "contextProfile",
];
/** Plain words for those fields, for "… are kept here until your organisation can hold them". */
export const V2_FIELD_WORDS = {
    role: "role",
    roleKind: "role",
    skills: "skills",
    tools: "skills",
    avatar: "picture",
    useCharacter: "picture",
    color: "picture",
    thinkingLevel: "thinking level",
    agentInstructions: "instructions",
    useWorktree: "worktree",
    permissionMode: "advanced settings",
    cwd: "advanced settings",
    providerId: "provider sign-in",
    credential: "provider sign-in",
    account: "provider sign-in",
    ref: "specialists",
    azureDevOpsUser: "Azure DevOps user",
    foundry: "Foundry agent",
    contextProfile: "context",
    formerNames: "former names",
    node: "node",
    settings: "team settings",
};
/**
 * The words a console offers for a member (the role vocabulary, runtimes, thinking levels, models):
 * what the policy manifest's `teams` block carries once AIOE's schema takes it.
 */
export function teamVocabulary(input) {
    return {
        roles: ROLE_IDS.map((id) => ({ id, words: id.replace(/-/g, " ") })),
        runtimes: input.runtimes.map((r) => ({ id: r.id, name: r.name })),
        thinkingLevels: [...MEMBER_THINKING_LEVELS],
        models: (input.models ?? []).map((m) => ({ runtime: m.runtime, id: m.id, ...(m.name ? { name: m.name } : {}) })),
    };
}
/** A member's role in the vocabulary, when its words make it obvious. */
export function roleKindOf(role) {
    return roleIdFromWords(role);
}
//# sourceMappingURL=orgTeamsV2.js.map